import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import { CHAT } from '../constants/testIds';
import { API } from '../lib/api';

const STORAGE_KEY = 'cruise_vibes_chat_session';

function getSessionId() {
    let s = localStorage.getItem(STORAGE_KEY);
    if (!s) {
        s = 'chat_' + Math.random().toString(36).slice(2, 12) + Date.now();
        localStorage.setItem(STORAGE_KEY, s);
    }
    return s;
}

export default function ChatWidget() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const sessionId = useRef(getSessionId());
    const scrollRef = useRef(null);

    useEffect(() => {
        if (open && messages.length === 0) {
            setMessages([{
                role: 'assistant',
                content: "Hey! I'm Vibe, your cruise concierge. Looking for a party cruise, a romantic getaway, or a family trip? Tell me your dates, vibe, and budget — I'll plan it.",
            }]);
        }
    }, [open, messages.length]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages]);

    const send = async () => {
        const text = input.trim();
        if (!text || busy) return;
        setBusy(true);
        setInput('');
        setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);

        try {
            const res = await fetch(`${API}/chat/stream`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId.current, message: text }),
            });
            if (!res.ok || !res.body) throw new Error('chat error');
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const lines = buf.split('\n');
                buf = lines.pop() || '';
                for (const ln of lines) {
                    if (!ln.startsWith('data:')) continue;
                    const data = ln.slice(5).trim();
                    if (data === '[DONE]') continue;
                    try {
                        const j = JSON.parse(data);
                        if (j.delta) {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = {
                                    ...copy[copy.length - 1],
                                    content: copy[copy.length - 1].content + j.delta,
                                };
                                return copy;
                            });
                        }
                    } catch (_) { /* ignore */ }
                }
            }
        } catch (e) {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: 'assistant', content: "Sorry, I hit a wave. Try again in a moment." };
                return copy;
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid={CHAT.toggle}
                className="fixed bottom-20 right-4 sm:right-6 z-40 h-14 w-14 rounded-full btn-sunset shadow-2xl flex items-center justify-center"
                aria-label="Open chat"
            >
                {open ? <X className="text-white" /> : <MessageCircle className="text-white" />}
            </button>
            {open && (
                <div
                    data-testid={CHAT.panel}
                    className="fixed bottom-36 right-4 sm:right-6 z-40 w-[90vw] max-w-sm h-[70vh] max-h-[560px] bg-brand-surface border border-white/10 rounded-2xl flex flex-col overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
                >
                    <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3 bg-brand-surface2">
                        <Sparkles className="text-brand-aqua" size={18} />
                        <div>
                            <div className="font-display uppercase tracking-tight text-sm font-bold">Vibe · AI Concierge</div>
                            <div className="text-[10px] uppercase tracking-widest text-white/40">Powered by GPT-5.5</div>
                        </div>
                    </div>
                    <div ref={scrollRef} data-testid={CHAT.messages} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-hide">
                        {messages.map((m, i) => (
                            <div key={i} className={m.role === 'user' ? 'text-right' : 'text-left'}>
                                <div className={`inline-block max-w-[85%] text-sm px-4 py-2.5 rounded-2xl whitespace-pre-wrap ${m.role === 'user' ? 'btn-sunset text-white' : 'bg-white/5 text-white/90 border border-white/5'}`}>
                                    {m.content || <span className="inline-flex gap-1"><i className="fa-solid fa-circle text-[6px] animate-pulse"></i><i className="fa-solid fa-circle text-[6px] animate-pulse" style={{animationDelay:'.2s'}}></i><i className="fa-solid fa-circle text-[6px] animate-pulse" style={{animationDelay:'.4s'}}></i></span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-white/10 p-3 flex items-center gap-2 bg-brand-surface2">
                        <input
                            data-testid={CHAT.input}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && send()}
                            disabled={busy}
                            placeholder="Ask about destinations, dates, vibes…"
                            className="flex-1 bg-transparent border border-white/10 focus:border-brand-aqua outline-none rounded-full px-4 py-2 text-sm text-white placeholder:text-white/30"
                        />
                        <button
                            data-testid={CHAT.send}
                            onClick={send}
                            disabled={busy}
                            className="btn-sunset h-9 w-9 rounded-full flex items-center justify-center disabled:opacity-50"
                            aria-label="Send"
                        >
                            <Send size={16} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
